import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { adminGetGlobalState } from '@/client/admin.client';

function formatValue(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function valueKindBadge(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return `array(${value.length})`;
  return typeof value;
}

export default function GlobalStatePage() {
  const [state, setState] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminGetGlobalState();
      setState(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const entries = useMemo(() => {
    if (!state) return [] as Array<[string, unknown]>;
    const all = Object.entries(state).sort(([a], [b]) => a.localeCompare(b));
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    return all.filter(([k]) => k.toLowerCase().includes(q));
  }, [state, filter]);

  const selected = selectedKey && state ? state[selectedKey] : null;

  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Global State</h2>
          <p className="text-sm text-muted-foreground">
            Inspect top-level keys stored in the active verse&apos;s global state.
          </p>
        </div>
        <Button type="button" onClick={() => void load()} disabled={loading} size="sm">
          {loading ? 'Loading…' : 'Reload'}
        </Button>
      </header>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base">Keys</CardTitle>
              <Badge variant="secondary">{entries.length}</Badge>
            </div>
            <Input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by key…"
            />
          </CardHeader>
          <CardContent>
            {loading && !state ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : entries.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {state && Object.keys(state).length === 0
                  ? 'Global state is empty.'
                  : 'No matches.'}
              </p>
            ) : (
              <ScrollArea className="h-[480px]">
                <ul className="flex flex-col gap-1">
                  {entries.map(([key, value]) => {
                    const isActive = key === selectedKey;
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => setSelectedKey(key)}
                          className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors ${
                            isActive
                              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                              : 'hover:bg-muted'
                          }`}
                        >
                          <span className="break-all font-mono">{key}</span>
                          <Badge variant="outline" className="shrink-0 font-normal">
                            {valueKindBadge(value)}
                          </Badge>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Value</CardTitle>
            {selectedKey && (
              <p className="break-all font-mono text-xs text-muted-foreground">{selectedKey}</p>
            )}
          </CardHeader>
          <CardContent>
            {!selectedKey ? (
              <p className="text-sm text-muted-foreground">Select a key on the left.</p>
            ) : (
              <>
                <div className="mb-2 flex items-center gap-2">
                  <Badge variant="outline">{valueKindBadge(selected)}</Badge>
                </div>
                <Separator className="mb-3" />
                <ScrollArea className="h-[420px] rounded-md border bg-muted/40">
                  <pre className="p-3 text-xs break-all whitespace-pre-wrap">
                    {formatValue(selected)}
                  </pre>
                </ScrollArea>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
